import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Check, Zap, Star, Building2, Coins, Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Static display metadata per plan (non-pricing fields)
const PLAN_META = {
  single: {
    description: "Perfect for focused professionals",
    features: (credits) => [
      "Access to 1 business function",
      "All applications in chosen function",
      `${credits} credits/month`,
      "Copy & use prompts",
      "Email support",
    ],
    color: "border-slate-200",
    btnClass: "bg-slate-800 hover:bg-slate-900 text-white",
    recommended: false,
  },
  multi: {
    description: "For versatile business professionals",
    features: (credits) => [
      "Access to 3 business functions",
      "All applications in chosen functions",
      `${credits} credits/month`,
      "Copy & use prompts",
      "Priority support",
    ],
    color: "border-slate-200",
    btnClass: "bg-slate-800 hover:bg-slate-900 text-white",
    recommended: false,
  },
  full: {
    description: "All functions, unlimited productivity",
    features: (credits) => [
      "Access to ALL 7 business functions",
      "All 17+ AI applications",
      `${credits} credits/month`,
      "Copy & use prompts",
      "Priority support",
      "Early access to new applications",
    ],
    color: "border-blue-600 ring-2 ring-blue-600",
    btnClass: "bg-blue-600 hover:bg-blue-700 text-white",
    recommended: true,
  },
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading]       = useState(null);
  const [error, setError]           = useState("");
  const [plans, setPlans]           = useState(null);
  const [creditPacks, setCreditPacks] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const [plansRes, packsRes] = await Promise.all([
          axios.get(`${API}/plans`),
          axios.get(`${API}/credit-packs`),
        ]);
        setPlans(plansRes.data);
        setCreditPacks(packsRes.data);
      } catch {
        setError("Failed to load pricing. Please refresh.");
      }
    };
    fetchPricing();
  }, []);

  const handleSubscribe = async (planId) => {
    if (!user) { navigate("/auth?tab=register"); return; }
    setLoading(planId);
    setError("");
    try {
      const res = await axios.post(`${API}/payments/create-checkout`,
        { plan_id: planId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const handleCreditPurchase = async (packId) => {
    if (!user) { navigate("/auth?tab=register"); return; }
    setLoading(packId);
    setError("");
    try {
      const res = await axios.post(`${API}/payments/create-checkout`,
        { credit_pack_id: packId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start checkout.");
    } finally {
      setLoading(null);
    }
  };

  // Build display-ready plan list from API data + static meta
  const displayPlans = plans
    ? ["single", "multi", "full"]
        .filter(k => plans[k])
        .map(k => ({
          id: k,
          name: plans[k].name,
          price: `€${plans[k].price_eur % 1 === 0 ? plans[k].price_eur : plans[k].price_eur.toFixed(2)}`,
          credits: plans[k].credits,
          ...PLAN_META[k],
          features: PLAN_META[k].features(plans[k].credits),
        }))
    : [];

  // Build display-ready credit packs from API data
  const displayPacks = creditPacks
    ? Object.entries(creditPacks).map(([id, pack]) => ({
        id,
        credits: pack.credits,
        price: `€${pack.amount % 1 === 0 ? pack.amount : pack.amount.toFixed(2)}`,
        per: `€${(pack.amount / pack.credits).toFixed(2)}/credit`,
        popular: id === "credits_500",
      }))
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-100 text-blue-700 border-0">Pricing</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Start free, upgrade as you grow. All plans include access to our AI application library.
          </p>
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-8 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 text-center">
            {error}
          </div>
        )}

        {/* Loading skeleton */}
        {!plans && !error && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin w-6 h-6 text-blue-600 mr-2" />
            <span className="text-slate-500 text-sm">Loading pricing...</span>
          </div>
        )}

        {/* Plans */}
        {displayPlans.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {displayPlans.map(plan => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl border p-7 ${plan.color} ${plan.recommended ? "shadow-xl" : "shadow-sm"}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white border-0 px-4 py-1">
                      <Star size={12} className="mr-1" /> Most Popular
                    </Badge>
                  </div>
                )}

                <div className="mb-5">
                  <h3 className="font-bold text-slate-900 text-lg">{plan.name}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">{plan.description}</p>
                </div>

                <div className="mb-5">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-slate-500 text-sm">/month</span>
                </div>

                <div className="flex items-center gap-2 mb-5 text-sm text-blue-600 font-medium">
                  <Coins size={14} />
                  {plan.credits} credits included/month
                </div>

                <Button
                  className={`w-full mb-6 ${plan.btnClass}`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading === plan.id || user?.subscription_plan === plan.id}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {loading === plan.id ? "Processing..." :
                    user?.subscription_plan === plan.id ? "Current Plan" : "Get Started"}
                </Button>

                <Separator className="mb-5" />

                <ul className="space-y-2.5">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Credit Packs */}
        {displayPacks.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Credit Packs</h2>
              <p className="text-slate-600 text-sm">Purchase additional credits to access premium applications</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {displayPacks.map(pack => (
                <div
                  key={pack.id}
                  className={`relative bg-white border rounded-xl p-6 text-center card-shadow ${pack.popular ? "border-blue-300" : "border-slate-200"}`}
                  data-testid={`credit-pack-${pack.id}`}
                >
                  {pack.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 border-0">
                      Best Value
                    </Badge>
                  )}
                  <div className="flex items-center justify-center gap-1.5 mb-3">
                    <Coins size={18} className="text-blue-600" />
                    <span className="text-2xl font-bold text-slate-900">{pack.credits}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">{pack.per}</p>
                  <p className="text-xl font-bold text-slate-900 mb-4">{pack.price}</p>
                  <Button
                    variant="outline"
                    className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
                    onClick={() => handleCreditPurchase(pack.id)}
                    disabled={loading === pack.id}
                    data-testid={`buy-credits-${pack.id}-btn`}
                  >
                    {loading === pack.id ? "Processing..." : "Buy Credits"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enterprise */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-center">
          <Building2 size={32} className="text-blue-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-3">Enterprise Access</h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Unlimited users, full platform access, admin-controlled permissions, and custom onboarding for your team. Contact us to set up your enterprise account.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              data-testid="contact-enterprise-btn"
            >
              Contact Us for Enterprise
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PricingPage;
